import { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  styled,
  Hidden,
  useTheme,
  useMediaQuery,
  Link,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { motion } from "framer-motion";
import { hoods } from "../../constants";

import ProfessionalsCard from "./ProfessionalsCard";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const SyletedNavBtn = styled(Button)(({ theme }) => ({
  width: "2.5rem",
  minWidth: "2.5rem",
  height: "2.5rem",
  padding: 0,
  margin: 0,
  borderRadius: "2.5rem",
  backgroundColor: "white",
  border: "1px solid",
  borderColor: theme.palette.alternate.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
}));

export default function NeighborhoodHomes() {
  const navigate = useNavigate();

  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const swiperRef = useRef(null);

  const handleNavigation = (type) => {
    if (swiperRef.current) {
      switch (type) {
        case "prev":
          swiperRef.current.slidePrev();
          break;
        case "next":
          swiperRef.current.slideNext();
          break;
        default:
          break;
      }
    }
  };

  const BrowseMore = () => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      disableElevation
      sx={{
        "&": { borderColor: "primary.main" },
        borderRadius: "50px",
        textTransform: "capitalize",
        padding: "12px 20px",
        height: "3rem",
        color: "text.orange.1",
        width: { xs: "100%", md: "auto" },
      }}
      onClick={() => {
        navigate("/homes-for-sale");
      }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <Typography fontSize={"1rem"}>See more</Typography>
        <ArrowForwardRoundedIcon
          sx={{
            color: "text.orange.1",
          }}
        />
      </Stack>
    </Button>
  );

  return (
    <Box sx={{ py: { xs: 4, md: 5 } }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Stack gap={3}>
          <Container
            maxWidth="xl"
            sx={{
              position: "relative",
              px: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <Stack gap={2}>
                <Typography
                  variant="h4"
                  component="h2"
                  fontWeight="bold"
                  sx={{ fontSize: "2rem" }}
                >
                  Find Homes For Sale in{" "}
                  <Hidden mdDown>
                    <br />
                  </Hidden>{" "}
                  your preferred Neighborhood.
                </Typography>
                <Typography
                  variant="body1"
                  fontSize={{ xs: "1rem:", sm: "18px" }}
                  color={"neutral.dark"}
                >
                  The neighborhoods with homes for sale, and the agents who know
                  them best.
                </Typography>
              </Stack>

              <Hidden mdDown>
                <BrowseMore />
              </Hidden>
            </Box>
          </Container>

          <Box
            sx={{
              position: "relative",
              "& .swiper-slide": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
            }}
          >
            <Container
              maxWidth="xl"
              sx={{
                position: "relative",
                px: 2.5,
              }}
            >
              <Swiper
                modules={[Autoplay, Navigation]}
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                loop
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                // slidesPerView="auto"
                // spaceBetween={sm ? "32" : "20"}
                breakpoints={{
                  0: {
                    slidesPerView: 1.1,
                    spaceBetween: 10,
                    centeredSlides: true,
                    centeredSlidesBounds: true,
                  },
                  600: {
                    slidesPerView: 2,
                    spaceBetween: 10,
                  },
                  900: {
                    slidesPerView: 3,
                    spaceBetween: 16,
                  },
                  1050: {
                    slidesPerView: 3.5,
                    spaceBetween: 16,
                  },
                  1200: {
                    slidesPerView: 4,
                    spaceBetween: 16,
                  },
                }}
                style={{ padding: "8px 0" }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  style={{ width: "100%" }}
                >
                  {hoods?.map((item) => {
                    return (
                      <SwiperSlide
                        key={item.city}
                        style={{
                          height: sm ? "370px" : "400px",
                          width: "100%",
                        }}
                      >
                        <motion.div
                          key={item.city}
                          whileHover={{ y: -5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <Link
                            href={`/homes-for-sale/${item.city}`}
                            target="_blank"
                          >
                            <ProfessionalsCard
                              imageSrc={item.img}
                              title={`${item.city} - Homes For Sale`}
                              buttonText="View List"
                              initialWidth="100%"
                              initialHeight="100%"
                              hoverScale={1}
                              hovByDefault
                            />
                          </Link>
                        </motion.div>
                      </SwiperSlide>
                    );
                  })}
                </motion.div>
              </Swiper>
            </Container>

            <Container
              maxWidth="xl"
              sx={{
                position: "relative",
                pt: 3,
                px: 2.5,
              }}
            >
              <Hidden mdUp>
                <BrowseMore />
              </Hidden>
              <Hidden mdDown>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  gap={2.5}
                >
                  <SyletedNavBtn
                    color="alternate"
                    onClick={() => handleNavigation("prev")}
                  >
                    <ArrowBackOutlinedIcon color="alternate" />
                  </SyletedNavBtn>
                  <SyletedNavBtn
                    color="alternate"
                    onClick={() => handleNavigation("next")}
                  >
                    <ArrowForwardRoundedIcon color="alternate" />
                  </SyletedNavBtn>
                </Stack>
              </Hidden>
            </Container>
          </Box>
        </Stack>
      </motion.div>
    </Box>
  );
}
